import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL, SITE_NAME, DEFAULT_LANGUAGE, LANGUAGES } from '../../config/constants';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  type?: string;
  url?: string;
  image?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  poem?: any;
  articleSection?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  type = 'website',
  url,
  image = `${SITE_URL}/og-image.jpg`,
  author,
  publishedTime,
  poem,
  articleSection,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `appTitle | ${SITE_NAME}`;
  const metaDescription = description || 'appSubtitle';
  const canonicalUrl = url || SITE_URL;

  const currentLang = DEFAULT_LANGUAGE || 'ta';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Alternate Language Links */}
      {LANGUAGES?.map(lang => (
        <link key={lang} rel="alternate" hrefLang={lang} href={`${SITE_URL}/${lang === 'x-default' ? '' : lang}`.replace(/\/$/, '')} />
      ))}
      {/* X-Default language fallback */}
      <link rel="alternate" hrefLang="x-default" href={SITE_URL} />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />

      {/* Article tags if type is article */}
      {type === 'article' && author && <meta property="article:author" content={author} />}
      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {type === 'article' && (articleSection || poem?.category) && <meta property="article:section" content={articleSection || poem?.category} />}
      {type === 'article' && poem?.tags?.map((tag: string) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Structured Data / JSON-LD */}
      {poem && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Poem",
            "name": poem.title || title,
            "text": poem.content,
            "author": {
              "@type": "Person",
              "name": (typeof poem.author === 'string' ? poem.author : poem.author?.name) || author || SITE_NAME
            },
            "inLanguage": poem.language || currentLang,
            "publisher": {
              "@type": "Organization",
              "name": SITE_NAME
            }
          })}
        </script>
      )}

      {/* PWA Tags */}
      <meta name="theme-color" content="#667eea" />
      <meta name="application-name" content={SITE_NAME} />
      <meta name="apple-mobile-web-app-title" content="Kavithai" />
    </Helmet>
  );
};

export default SEOHead;
